import { useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '../services/api';
import type { LevelUpCelebrationPayload, QuizLevelUpDetectInput } from '../utils/quizLevelUpCelebration';
import { detectQuizLevelUp } from '../utils/quizLevelUpCelebration';

/**
 * Manages pending level-up celebration after quiz/PvP finish.
 * Captures profile level on mount; compares with finish `newLevel` before opening results.
 */
export function useQuizFinishLevelUp() {
  const userLevelRef = useRef<number | null>(null);
  const pendingLevelUpRef = useRef<LevelUpCelebrationPayload | null>(null);

  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpOld, setLevelUpOld] = useState(1);
  const [levelUpNew, setLevelUpNew] = useState(1);

  useEffect(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) return;
    authService
      .getProfile()
      .then((u) => {
        userLevelRef.current = u.level;
      })
      .catch(() => {});
  }, []);

  const setUserLevelSnapshot = useCallback((level: number) => {
    userLevelRef.current = level;
  }, []);

  const prepareFromQuizResponse = useCallback((response: QuizLevelUpDetectInput): boolean => {
    const detected = detectQuizLevelUp(userLevelRef.current, response);
    pendingLevelUpRef.current = detected;
    if (detected != null && response.newLevel != null) {
      userLevelRef.current = response.newLevel;
    }
    return detected != null;
  }, []);

  const tryOpenLevelUpCelebration = useCallback((): boolean => {
    const pending = pendingLevelUpRef.current;
    if (!pending) return false;
    setLevelUpOld(pending.oldLevel);
    setLevelUpNew(pending.newLevel);
    setShowLevelUp(true);
    return true;
  }, []);

  const closeLevelUpCelebration = useCallback(() => {
    setShowLevelUp(false);
    pendingLevelUpRef.current = null;
  }, []);

  const clearPendingLevelUp = useCallback(() => {
    pendingLevelUpRef.current = null;
    setShowLevelUp(false);
  }, []);

  const hasPendingLevelUp = useCallback(() => pendingLevelUpRef.current != null, []);

  return {
    showLevelUp,
    levelUpOld,
    levelUpNew,
    setUserLevelSnapshot,
    prepareFromQuizResponse,
    tryOpenLevelUpCelebration,
    closeLevelUpCelebration,
    clearPendingLevelUp,
    hasPendingLevelUp,
  };
}
